import {
  computed, ref, watch,
} from 'vue';
import { useStore } from 'vuex';
import { injectLocal, provideLocal } from '@vueuse/core';
import { Cookie } from '@common-vue/util/src/modules/cookie';
import { cookies } from 'config';
import { useBrand } from '@composables/useBrand';
import useMembersOfProduct from '@composables/useMembersOfProduct';
import { getStoreLocation } from '@modules/availability/utils';
import { useProduct } from '@modules/product';
import { useFeatureEligibility, useProductFlags } from '@modules/featureControl';
import { useUpc } from '@modules/upc/composables';
import { getBigTicketZipCookie } from '@utils/cookiesUtils';

import { availabilityBigTicketParser } from '../utils/availabilityBigTicketParser';

/**
 * @type {import('vue').InjectionKey<import('./useAvailabilityBigTicket').AvailabilityReturnData>}
 */
const AVAILABILITY_BT_REGISTRY = Symbol.for('AVAILABILITY_BT_REGISTRY');

export const useAvailabilityBigTicket = () => injectLocal(AVAILABILITY_BT_REGISTRY);

export const useAvailabilityBigTicketProvide = () => {
  const { isBcom } = useBrand();
  const { isBigTicket, isBigTicketV2C } = useFeatureEligibility();
  const { productId } = useProduct();
  // memberProductIdsString => multiple products ids if consolidated, else single productId
  const { memberProductIdsString } = useMembersOfProduct();
  const { selectedUpcId } = useUpc(productId);
  const store = useStore();
  const zipCode = computed(() => store.state.availabilityBigTicketStore.zipCode);
  const { isMemberOfMasterFlag, isBigTicketItemFlag } = useProductFlags();

  /** @type {import('vue').Ref<{ [key: number]: import('./useAvailabilityBigTicket').BigTicketAvailability }>} */
  const availabilityUpcs = ref({});

  const availabilityData = computed(() => store.state.availabilityBigTicketStore.data);

  const selectedUpcData = computed(() => availabilityUpcs.value[selectedUpcId.value]);
  const showUnavailableMessageForCurrentUpc = computed(() => selectedUpcData.value?.showUnavailableMessage);
  const callCenterCTAMessage = `
    Have a question or need advice? Call one of our furniture and mattress experts at `
    + `${isBcom ? '<strong>1-866-593-2540</strong>, 8AM-Midnight EST, 7 days/week.' : '<strong>1-800-BUY-MACY</strong>.'}`;
  const bcomErrorMessage = 'To check availability, call 1-866-593-2540.';
  const deliverabilityMessageForCurrentUpc = computed(() => {
    if (isBcom && selectedUpcData.value?.deliverability?.status === 'ERROR') {
      return bcomErrorMessage;
    }
    return selectedUpcData.value?.deliverability?.deliverabilityMessage;
  });
  const bigTicketAvailableForCurrentUpc = computed(() => Boolean(selectedUpcData.value?.bigTicketAvailable));
  const isWhiteGloveDelivery = computed(() => isBcom && !isBigTicketV2C && isBigTicket && !isMemberOfMasterFlag);

  const showNotAvailableComponent = computed(() => zipCode.value
    && showUnavailableMessageForCurrentUpc.value
    && isBigTicketItemFlag);
  const showNoZipCodeComponent = computed(() => !zipCode.value && isBigTicketItemFlag);
  // TODO - this value no longer available - add it back in later when itd computed by upc again
  // const showOnDisplayComponent = computed(() => isBigTicketItemFlag);
  const showShipToZipCodeComponent = computed(() => !!zipCode.value
    && !showNotAvailableComponent.value
    && isBigTicketItemFlag);

  /**
   * @param { string } zipCodeValue
   */

  function updateAvailabilityUpcs(zipCodeValue) {
    availabilityUpcs.value = availabilityBigTicketParser({
      availabilityData: availabilityData.value,
      zipCode: zipCodeValue,
      lookupIds: memberProductIdsString.value,
    });
  }

  async function fetchAvailability() {
    await store.dispatch('availabilityBigTicketStore/fetchAvailability', { lookupIds: memberProductIdsString.value, zipCode: zipCode.value });
  }

  const isDataLoaded = ref(false);

  watch(zipCode, async (newZipCode) => {
    if (!newZipCode) return;
    await fetchAvailability();
    updateAvailabilityUpcs(newZipCode);
    isDataLoaded.value = true;
  }, { immediate: true });

  const dataLoadNeeded = computed(() => getBigTicketZipCookie() || getStoreLocation().zipcode);

  function setZipCode() {
    const zipCodeFromCookie = getBigTicketZipCookie();
    const storeLocationZipCode = getStoreLocation().zipcode;
    store.commit('availabilityBigTicketStore/setZip', zipCodeFromCookie || storeLocationZipCode);

    if (zipCodeFromCookie) return;

    const isValidZipCode = (zip) => /^\d{5}$/.test(zip);

    if (isValidZipCode(storeLocationZipCode)) {
      Cookie.set(cookies.bigTicketZipCode, storeLocationZipCode, cookies.miscGCs);
    }
  }

  const zipcodeSlideoutShown = computed({
    get() {
      return store.state.availabilityBigTicketStore.zipcodeSlideoutShown;
    },
    set(value) {
      store.commit('availabilityBigTicketStore/setZipCodeSlideoutShown', value);
    },
  });
  const previouslyFocusedElement = computed({
    get() {
      return store.state.availabilityBigTicketStore.previouslyFocusedElement;
    },
    set(value) {
      store.commit('availabilityBigTicketStore/setPreviouslyFocusedElement', value);
    },
  });

  const activeSlideout = computed({
    get() {
      return store.state.availabilityBigTicketStore.activeSlideout;
    },
    set(value) {
      store.commit('availabilityBigTicketStore/setActiveSlideout', value);
    },
  });

  const setStoreZipCode = (slideoutZipCode) => {
    store.commit('availabilityBigTicketStore/setZip', slideoutZipCode);
  };

  const toggleBTSlideOut = (id) => {
    store.commit('slideoutStore/TOGGLE', `zipcode-slideout-${id}`);
  };

  provideLocal(AVAILABILITY_BT_REGISTRY, {
    bigTicketAvailableForCurrentUpc,
    deliverabilityMessageForCurrentUpc,
    showNotAvailableComponent,
    showShipToZipCodeComponent,
    showNoZipCodeComponent,
    zipCode,
    callCenterCTAMessage,
    isWhiteGloveDelivery,
    setZipCode,
    activeSlideout,
    zipcodeSlideoutShown,
    previouslyFocusedElement,
    setStoreZipCode,
    toggleBTSlideOut,
    dataLoadNeeded,
    isDataLoaded,
  });
};
